import { apiClient } from './client';

export interface Student {
  id: number;
  username: string;
  display_name: string;
  classroom_name: string;
}

export interface ProgressRow {
  student_id: number;
  student_name: string;
  project_id: number | null;
  problem_text: string | null;
  current_role: string | null;
  current_role_id: number | null;
  passed_count: number;
  total_roles: number;
  average_score: string | null;
  status: string;
  last_updated: string | null;
}

export interface ChatMessage {
  id: number;
  project_id: number;
  role_name: string;
  sender: 'student' | 'ai';
  content: string;
  attached_file: string | null;
  evidence_type?: 'anket' | 'foto' | 'ses' | 'video' | 'cizim' | 'metin' | 'yok';
  created_at: string;
}

export interface StudentRoleSession {
  id: number;
  role_name: string;
  role_focus: string;
  persona_type: string;
  order: number;
  status: string;
  score: number | null;
  attempts_used: number;
}

export interface StudentProjectDetail {
  id: number;
  problem_text: string;
  status: string;
  created_at: string;
  finished_at: string | null;
  average_score: string | number | null;
  diploma_pdf: string | null;
  role_sessions: StudentRoleSession[];
}

export interface ClassResource {
  id: number;
  student_id: number;
  student_name: string;
  project_id: number;
  role_name: string;
  evidence_type: string;
  attached_file: string;
  content: string;
  created_at: string;
}

export interface ClassInsights {
  total_students: number;
  students_with_project: number;
  projects_passed: number;
  projects_failed: number;
  problem_popularity: Record<string, number>;
  evidence_distribution: Record<string, number>;
  average_evidence_per_student: number;
  first_attempt_pass_rate: number;
  hardest_role: string | null;
  common_themes_in_failures: Record<string, number>;
}

export async function listStudents(): Promise<Student[]> {
  const res = await apiClient.get('/teacher/students/');
  return Array.isArray(res.data) ? res.data : [];
}

export async function createStudent(
  displayName: string,
  username: string,
  password?: string,
): Promise<{ student: Student; temp_password: string }> {
  const res = await apiClient.post('/teacher/students/', {
    display_name: displayName,
    username,
    ...(password ? { password } : {}),
  });
  return res.data;
}

export async function resetStudentPassword(
  studentId: number,
  customPassword?: string,
): Promise<{ temp_password: string }> {
  const res = await apiClient.post(
    `/teacher/students/${studentId}/reset-password/`,
    customPassword ? { password: customPassword } : {},
  );
  return res.data;
}

export async function deleteStudent(studentId: number): Promise<void> {
  await apiClient.delete(`/teacher/students/${studentId}/delete/`);
}

export async function getClassProgress(): Promise<ProgressRow[]> {
  const res = await apiClient.get('/teacher/progress/');
  return Array.isArray(res.data) ? res.data : [];
}

export async function resetProject(projectId: number): Promise<void> {
  await apiClient.post(`/teacher/projects/${projectId}/reset/`);
}

export async function deleteTeacherProject(projectId: number): Promise<{
  deleted: boolean;
  project: string;
  files_cleaned: number;
  message: string;
}> {
  const res = await apiClient.delete(`/teacher/projects/${projectId}/delete/`);
  return res.data;
}

export async function getStudentDetail(
  studentId: number,
): Promise<{ student: Student; messages: ChatMessage[]; projects: StudentProjectDetail[] }> {
  const res = await apiClient.get(
    `/teacher/students/${studentId}/detail/`,
  );
  const data = res.data ?? {};
  return {
    student: data.student ?? { id: 0, username: '', display_name: '', classroom_name: '' },
    messages: Array.isArray(data.messages) ? data.messages : [],
    projects: Array.isArray(data.projects) ? data.projects : [],
  };
}

export async function getClassResources(): Promise<ClassResource[]> {
  const res = await apiClient.get('/teacher/resources/');
  return Array.isArray(res.data) ? res.data : [];
}

export async function getClassInsights(): Promise<ClassInsights> {
  const res = await apiClient.get('/teacher/insights/');
  return res.data ?? {
    total_students: 0,
    students_with_project: 0,
    projects_passed: 0,
    projects_failed: 0,
    problem_popularity: {},
    evidence_distribution: {},
    average_evidence_per_student: 0,
    first_attempt_pass_rate: 0,
    hardest_role: null,
    common_themes_in_failures: {},
  };
}